import type { GapKind, GapRow } from "./overview";

/**
 * Everything the Data gaps page derives from the grouped gap rows and the URL
 * filters. Pure, so the page stays JSX and the logic stays testable.
 */

export const GAP_KINDS = ["no-parser", "unpriced", "empty"] as const satisfies readonly GapKind[];

export const KIND_LABEL: Record<GapKind, string> = {
	"no-parser": "no parser",
	unpriced: "unpriced",
	empty: "empty",
};

export type GapsSearch = { kind?: GapKind; harness?: string; q?: string };

export function parseGapsSearch(search: Record<string, unknown>): GapsSearch {
	const out: GapsSearch = {};
	if (typeof search.kind === "string" && (GAP_KINDS as readonly string[]).includes(search.kind))
		out.kind = search.kind as GapKind;
	if (typeof search.harness === "string" && search.harness) out.harness = search.harness;
	if (typeof search.q === "string" && search.q) out.q = search.q;
	return out;
}

/** Drops empty values so the plain view stays bare `/ledger/gaps`. */
export function cleanGapsSearch(search: GapsSearch): GapsSearch {
	return parseGapsSearch(search as Record<string, unknown>);
}

/** Kind + harness match, and a case-insensitive `q` over the path and every model. */
export function filterGapRows(rows: GapRow[], search: GapsSearch): GapRow[] {
	const needle = (search.q ?? "").trim().toLowerCase();
	return rows.filter(
		(row) =>
			(!search.kind || row.kind === search.kind) &&
			(!search.harness || row.harness === search.harness) &&
			(!needle ||
				row.path.toLowerCase().includes(needle) ||
				row.models.some((model) => model.toLowerCase().includes(needle))),
	);
}

/** Tile + subtitle counts for a (filtered) set. Each stored gap is one turn. */
export function gapTotals(rows: GapRow[]): {
	files: number;
	gaps: number;
	noParser: number;
	unpricedTurns: number;
} {
	let gaps = 0;
	let noParser = 0;
	let unpricedTurns = 0;
	for (const row of rows) {
		gaps += row.turns;
		if (row.kind === "no-parser") noParser += row.turns;
		else if (row.kind === "unpriced") unpricedTurns += row.turns;
	}
	return { files: rows.length, gaps, noParser, unpricedTurns };
}

/** Harnesses present in the gaps, most gaps first, then name. */
export function gapHarnessOptions(rows: GapRow[]): string[] {
	const counts = new Map<string, number>();
	for (const row of rows) counts.set(row.harness, (counts.get(row.harness) ?? 0) + row.turns);
	return [...counts.entries()]
		.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
		.map(([harness]) => harness);
}

export type GapSortKey = "kind" | "path" | "harness" | "models" | "turns";
export type SortDir = 1 | -1;

export const DEFAULT_GAP_SORT: { key: GapSortKey; dir: SortDir } = { key: "turns", dir: -1 };

/** Direction a column sorts in on its first click: text ascending, turns descending. */
export function firstSortDir(key: GapSortKey): SortDir {
	return key === "turns" ? -1 : 1;
}

function compareBy(a: GapRow, b: GapRow, key: GapSortKey): number {
	switch (key) {
		case "turns":
			return a.turns - b.turns;
		case "models":
			return a.models.join(" ").localeCompare(b.models.join(" "));
		case "kind":
			return KIND_LABEL[a.kind].localeCompare(KIND_LABEL[b.kind]);
		default:
			return a[key].localeCompare(b[key]);
	}
}

/** Sorted copy. Ties always break by path ascending, whatever the direction. */
export function sortGapRows(rows: GapRow[], key: GapSortKey, dir: SortDir): GapRow[] {
	return [...rows].sort(
		(a, b) =>
			dir * compareBy(a, b, key) || a.path.localeCompare(b.path) || a.key.localeCompare(b.key),
	);
}

/** The visible page, clamped, with 1-based bounds for "Showing a–b of N" (0–0 when empty). */
export function pageSlice<T>(
	rows: T[],
	page: number,
	size: number,
): { rows: T[]; page: number; pages: number; start: number; end: number } {
	const pages = Math.max(1, Math.ceil(rows.length / size));
	const current = Math.min(Math.max(0, page), pages - 1);
	const visible = rows.slice(current * size, (current + 1) * size);
	const start = visible.length ? current * size + 1 : 0;
	return {
		rows: visible,
		page: current,
		pages,
		start,
		end: visible.length ? start + visible.length - 1 : 0,
	};
}

export const GAP_CSV_HEADER = ["kind", "path", "harness", "turns", "models"];

export function gapCsvRows(rows: GapRow[]): (string | number)[][] {
	return rows.map((row) => [
		KIND_LABEL[row.kind],
		row.path,
		row.harness,
		row.turns,
		row.models.join(" "),
	]);
}
